import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, animate } from 'framer-motion';
import {
  Calculator,
  BookOpen,
  Atom,
  Globe,
  Scroll,
  Translate,
  CheckCircle,
  Lightning,
  TrendUp,
  Clock,
  ArrowRight,
} from '@phosphor-icons/react';

import { mathsSections } from '../data/mathsContent';
import { englishSections } from '../data/englishContent';
import {
  biologySections,
  chemistrySections,
  physicsSections,
} from '../data/scienceContent';
import { geographySections } from '../data/subjects/geography/content';

function countTopics(sections = []) {
  if (!Array.isArray(sections)) return 0;

  return sections.reduce((total, section) => {
    return total + (Array.isArray(section?.topics) ? section.topics.length : 0);
  }, 0);
}

function countSections(sections = []) {
  return Array.isArray(sections) ? sections.length : 0;
}

function getScienceTopicCount() {
  return (
    countTopics(biologySections) +
    countTopics(chemistrySections) +
    countTopics(physicsSections)
  );
}

function getScienceSectionCount() {
  return (
    countSections(biologySections) +
    countSections(chemistrySections) +
    countSections(physicsSections)
  );
}

const COMING = [
  {
    name: 'History',
    icon: Scroll,
    accent: 'bg-amber-500',
    eta: 'On deck',
    note: 'Thematic & period studies',
  },
  {
    name: 'Spanish',
    icon: Translate,
    accent: 'bg-blue-500',
    eta: 'Planned',
    note: 'Speaking · Writing · Listening',
  },
];

function Counter({ to, duration = 1.4, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;

    const controls = animate(0, to, {
      duration,
      ease: 'easeOut',
      onUpdate: (v) => setVal(Math.round(v)),
    });

    return () => controls.stop();
  }, [inView, to, duration]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wide uppercase text-emerald-600 dark:text-emerald-400">
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
      </span>
      Live now
    </span>
  );
}

function buildLiveSubjects() {
  return [
    {
      slug: 'maths',
      name: 'Maths',
      icon: Calculator,
      path: '/free-lesson?subject=maths',
      accent: 'bg-indigo-500',
      tint: 'bg-indigo-50 dark:bg-indigo-900/20',
      ring: 'ring-indigo-200 dark:ring-indigo-800/40',
      sections: countSections(mathsSections),
      topics: countTopics(mathsSections),
      extras: 'GCSE Problem Mode · Mock Exams · Parent GCSE-readiness scorecard',
    },
    {
      slug: 'english-language',
      name: 'English',
      icon: BookOpen,
      path: '/free-lesson?subject=english',
      accent: 'bg-rose-500',
      tint: 'bg-rose-50 dark:bg-rose-900/20',
      ring: 'ring-rose-200 dark:ring-rose-800/40',
      sections: countSections(englishSections),
      topics: countTopics(englishSections),
      extras:
        'Macbeth · A Christmas Carol · An Inspector Calls · Power & Conflict · AQA past papers',
    },
    {
      slug: 'science',
      name: 'Combined Science',
      icon: Atom,
      path: '/free-lesson?subject=science',
      accent: 'bg-emerald-500',
      tint: 'bg-emerald-50 dark:bg-emerald-900/20',
      ring: 'ring-emerald-200 dark:ring-emerald-800/40',
      sections: getScienceSectionCount(),
      topics: getScienceTopicCount(),
      extras: 'Biology · Chemistry · Physics · Required Practicals · Mock Exams',
    },
    {
      slug: 'geography',
      name: 'Geography',
      icon: Globe,
      path: '/free-lesson?subject=geography',
      accent: 'bg-cyan-500',
      tint: 'bg-cyan-50 dark:bg-cyan-900/20',
      ring: 'ring-cyan-200 dark:ring-cyan-800/40',
      sections: countSections(geographySections),
      topics: countTopics(geographySections),
      extras:
        'Physical geography · Human geography · Fieldwork · Map skills · AQA-style exam practice',
    },
  ];
}

export default function LessonCountShowcase({
  compact = false,
  heading,
  subheading,
  showRoadmap = true,
}) {
  const liveSubjects = useMemo(() => buildLiveSubjects(), []);

  const totalLiveTopics = useMemo(() => {
    return liveSubjects.reduce((sum, subject) => sum + subject.topics, 0);
  }, [liveSubjects]);

  const totalSections = useMemo(() => {
    return liveSubjects.reduce((sum, subject) => sum + subject.sections, 0);
  }, [liveSubjects]);

  return (
    <section
      className={compact ? 'py-8 md:py-10' : 'py-14 md:py-20'}
      data-testid="lesson-count-showcase"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full mb-3">
            LESSONS &amp; COVERAGE
          </span>

          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-3"
            data-testid="lesson-count-heading"
          >
            {heading || 'Real lessons, written and live today'}
          </h2>

          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            {subheading ||
              'Maths, English, Science and Geography are live now — with structured topics, worked examples, practice questions and AI feedback. History and Spanish are next on the roadmap.'}
          </p>
        </motion.div>

        {/* Top-line tally */}
        <motion.div
          className="neo-card p-5 md:p-6 mb-8 bg-primary text-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          data-testid="lesson-count-tally"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-black leading-none">
                <Counter to={totalLiveTopics} />
              </div>
              <div className="text-xs md:text-sm opacity-85 mt-1.5 font-bold uppercase tracking-wide">
                Lessons live
              </div>
            </div>

            <div>
              <div className="text-4xl md:text-5xl font-black leading-none">
                <Counter to={totalSections} />
              </div>
              <div className="text-xs md:text-sm opacity-85 mt-1.5 font-bold uppercase tracking-wide">
                Sections authored
              </div>
            </div>

            <div>
              <div className="text-4xl md:text-5xl font-black leading-none">
                <Counter to={liveSubjects.length} />
              </div>
              <div className="text-xs md:text-sm opacity-85 mt-1.5 font-bold uppercase tracking-wide">
                Subjects live
              </div>
            </div>

            <div>
              <div className="text-4xl md:text-5xl font-black leading-none">
                <Counter to={COMING.length} />
                <span className="text-2xl align-top">+</span>
              </div>
              <div className="text-xs md:text-sm opacity-85 mt-1.5 font-bold uppercase tracking-wide">
                On the way
              </div>
            </div>
          </div>
        </motion.div>

        {/* Live subject cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {liveSubjects.map((subject, index) => {
            const Icon = subject.icon;

            return (
              <motion.div
                key={subject.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`live-subject-${subject.slug}`}
              >
                <Link
                  to={subject.path}
                  className={`neo-card p-6 md:p-8 ${subject.tint} relative overflow-hidden block h-full hover:-translate-y-1 transition-all group`}
                >
                  <div
                    className={`absolute -top-10 -right-10 w-40 h-40 ${subject.accent} opacity-10 rounded-full blur-2xl`}
                  />

                  <div className="flex items-start justify-between mb-4 relative">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-12 h-12 ${subject.accent} text-white rounded-xl flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)]`}
                      >
                        <Icon size={26} weight="fill" />
                      </div>

                      <div>
                        <h3 className="font-heading font-black text-2xl md:text-3xl leading-none">
                          {subject.name}
                        </h3>
                        <div className="mt-1">
                          <LiveBadge />
                        </div>
                      </div>
                    </div>

                    <CheckCircle
                      size={28}
                      weight="fill"
                      className="text-emerald-500 shrink-0"
                    />
                  </div>

                  <div className="flex items-end gap-3 mb-5 relative">
                    <div className="font-black leading-none">
                      <span
                        className="text-6xl md:text-7xl"
                        data-testid={`live-subject-${subject.slug}-topic-count`}
                      >
                        <Counter to={subject.topics} />
                      </span>
                    </div>

                    <div className="pb-2">
                      <div className="text-base font-bold">topics</div>
                      <div className="text-xs text-muted-foreground">
                        {subject.sections} sections
                      </div>
                    </div>
                  </div>

                  {subject.extras && (
                    <div
                      className="text-xs md:text-sm font-semibold text-foreground/80 mb-4 leading-relaxed relative"
                      data-testid={`live-subject-${subject.slug}-extras`}
                    >
                      {subject.extras}
                    </div>
                  )}

                  <div className="relative">
                    <div className="flex items-center justify-between text-xs font-bold mb-1.5">
                      <span className="text-muted-foreground">Curriculum coverage</span>
                      <span className="text-emerald-600 dark:text-emerald-400">100%</span>
                    </div>

                    <div className="h-2 rounded-full bg-foreground/10 overflow-hidden">
                      <motion.div
                        className={`h-full ${subject.accent}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 1.4,
                          ease: 'easeOut',
                          delay: 0.2 + index * 0.1,
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-5 text-sm font-black text-primary group-hover:gap-3 transition-all">
                    Try a free lesson
                    <ArrowRight size={16} weight="bold" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Coming soon roadmap */}
        {showRoadmap && (
          <motion.div
            className="neo-card p-5 md:p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="lesson-count-roadmap"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <TrendUp size={20} weight="fill" className="text-primary" />
                <h3 className="font-heading font-bold text-lg md:text-xl">
                  More subjects on the way
                </h3>
              </div>

              <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                <Clock size={14} weight="fill" /> Rolling out through 2026
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {COMING.map((subject, index) => {
                const Icon = subject.icon;

                return (
                  <motion.div
                    key={subject.name}
                    className="p-3 md:p-4 rounded-xl border-2 border-dashed border-foreground/15 hover:border-foreground/30 transition-colors"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    data-testid={`coming-subject-${subject.name
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div
                        className={`w-9 h-9 ${subject.accent} text-white rounded-lg flex items-center justify-center opacity-90`}
                      >
                        <Icon size={18} weight="fill" />
                      </div>

                      <span className="font-heading font-bold text-sm md:text-base truncate">
                        {subject.name}
                      </span>
                    </div>

                    <div className="text-[11px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400 mb-1">
                      {subject.eta}
                    </div>

                    <div className="text-xs text-muted-foreground leading-snug">
                      {subject.note}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-foreground/10 text-xs text-muted-foreground flex items-start gap-2">
              <Lightning
                size={14}
                weight="fill"
                className="text-primary mt-0.5 shrink-0"
              />
              <span>
                Every new subject uses the same structured learning journey, adaptive difficulty
                and AI marking used across the live subjects — so your child&apos;s learning habit
                travels with them.
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}